/**
 * API Key setup modal for initial configuration
 */

import { useState } from 'react';
import {
  setRealtimeApiKey,
  setPollInterval,
  setSelectedOperator,
  POLL_INTERVALS,
  RATE_LIMIT_INFO,
  OPERATORS_WITH_TRACKING
} from '../config';
import './ApiKeySetup.css';

interface ApiKeySetupProps {
  onComplete: (realtimeApiKey: string, operator: string, interval: number) => void;
}

export function ApiKeySetup({ onComplete }: ApiKeySetupProps) {
  const [realtimeApiKey, setRealtimeApiKeyInput] = useState('');
  const [selectedOperator, setSelectedOperatorInput] = useState('sl');
  const [selectedTier, setSelectedTier] = useState<'BRONZE' | 'SILVER' | 'GOLD'>('BRONZE');
  const [customInterval, setCustomInterval] = useState(POLL_INTERVALS.NORMAL);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!realtimeApiKey.trim()) {
      alert('Please enter a realtime API key');
      return;
    }

    // Save to localStorage
    setRealtimeApiKey(realtimeApiKey.trim());
    setSelectedOperator(selectedOperator);
    setPollInterval(customInterval);

    // Notify parent
    onComplete(realtimeApiKey.trim(), selectedOperator, customInterval);
  };

  const tierInfo = RATE_LIMIT_INFO[selectedTier];
  const recommendedInterval = tierInfo.recommended;

  // Calculate requests per month for selected interval
  const requestsPerMonth = Math.floor((30 * 24 * 60 * 60 * 1000) / customInterval);

  return (
    <div className="api-key-setup-overlay">
      <div className="api-key-setup-modal">
        <h1>Welcome to Transit Track</h1>
        <p className="subtitle">Configure your Trafiklab API key to get started</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="operator-select">
              Transit Operator / Region
              <span className="required">*</span>
            </label>
            <select
              id="operator-select"
              value={selectedOperator}
              onChange={(e) => setSelectedOperatorInput(e.target.value)}
              className="operator-select"
            >
              {OPERATORS_WITH_TRACKING.map(op => (
                <option key={op.id} value={op.id}>
                  {op.name} - {op.region}
                </option>
              ))}
            </select>
            <p className="help-text">
              Select your local transit operator to track vehicles in your region
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="realtime-api-key">
              Realtime API Key (GTFS Regional)
              <span className="required">*</span>
            </label>
            <input
              type="text"
              id="realtime-api-key"
              value={realtimeApiKey}
              onChange={(e) => setRealtimeApiKeyInput(e.target.value)}
              placeholder="Enter your realtime API key"
              className="api-key-input"
              autoFocus
            />
            <p className="help-text">
              For vehicle positions - Get at{' '}
              <a href="https://www.trafiklab.se/api/gtfs-regional/konsol" target="_blank" rel="noopener noreferrer">
                GTFS Regional API
              </a>
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="tier-select">API Tier</label>
            <select
              id="tier-select"
              value={selectedTier}
              onChange={(e) => {
                const tier = e.target.value as 'BRONZE' | 'SILVER' | 'GOLD';
                setSelectedTier(tier);
                setCustomInterval(RATE_LIMIT_INFO[tier].recommended);
              }}
              className="tier-select"
            >
              <option value="BRONZE">Bronze (Free) - 30k req/month</option>
              <option value="SILVER">Silver - 250k req/month</option>
              <option value="GOLD">Gold - 500k req/month</option>
            </select>
            <p className="help-text">{tierInfo.description}</p>
          </div>

          <div className="form-group">
            <label htmlFor="interval-select">Update Interval</label>
            <select
              id="interval-select"
              value={customInterval}
              onChange={(e) => setCustomInterval(parseInt(e.target.value))}
              className="interval-select"
            >
              <option value={POLL_INTERVALS.REALTIME}>10 seconds (Real-time)</option>
              <option value={POLL_INTERVALS.FREQUENT}>30 seconds (Frequent)</option>
              <option value={POLL_INTERVALS.NORMAL}>1 minute (Normal) ⭐</option>
              <option value={POLL_INTERVALS.MODERATE}>2 minutes (Moderate)</option>
              <option value={POLL_INTERVALS.CONSERVATIVE}>3 minutes (Conservative)</option>
              <option value={POLL_INTERVALS.MINIMAL}>5 minutes (Minimal)</option>
            </select>

            <div className="rate-limit-warning">
              <div className="rate-limit-stats">
                <div>
                  <strong>Estimated usage:</strong> ~{requestsPerMonth.toLocaleString()} requests/month
                </div>
                <div>
                  <strong>Your limit:</strong> {tierInfo.limit.toLocaleString()} requests/month
                </div>
                {requestsPerMonth > tierInfo.limit && (
                  <div className="warning-text">
                    ⚠️ Warning: This interval may exceed your rate limit!
                  </div>
                )}
                {customInterval === recommendedInterval && (
                  <div className="success-text">
                    ✓ Recommended for your tier
                  </div>
                )}
              </div>
            </div>
          </div>

          <button type="submit" className="submit-button">
            Start Tracking
          </button>
        </form>

        <div className="info-box">
          <h3>ℹ️ About API Keys</h3>
          <ul>
            <li>You can use the same key for both fields (if rate limits allow)</li>
            <li>Or use separate keys to distribute load</li>
            <li>Bronze tier (free): 30,000 requests per 30 days</li>
            <li>1 minute interval = ~43,200 req/month (safe for Bronze)</li>
            <li>Static data is cached for 1 month to save API calls</li>
            <li>You can change settings later in the app</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
